import { makeAutoObservable, action, toJS } from "mobx";
import { EditorState, Modifier, SelectionState, convertToRaw, convertFromRaw} from 'draft-js';
import { socket, setConnectCallBack, setConnectErrCallBack } from './socket';
import { decorator } from './Decoration';
import { examples, } from './Examples';
import { claimTemplate, wdTemplate, drawingTemplate, claims2JSON, wd2JSON } from './Templates';

// --------------------------------------------------------------------
// Convenience functions for dealing with DraftJS editors

const walkTree = action((node, callback) => {
  callback(node);
  for (const child of node.children)
    walkTree(child, callback);
});



const findWithRegex = (regex, contentBlock, callback) => {
  const text = contentBlock.getText();
  let start, end;
  const matches = text.matchAll(regex);
  //console.log('finding in regex', regex, text);
  for (const match of matches) {
    start = match.index;
    end = start + match[0].length;
    //console.log('Matched', match, start, end);
    callback(start, end);
  }
};

// everywhere means to replace any (even non-entity) text that matches the searchstring.
// If entityKey is null, then the entity is deleted (but text is retained)
const replaceString = action((claim, searchString, replaceString, entityKey, everywhere) => { 
  //console.log('Search String', searchString, replaceString, entityKey, everywhere);
  const regex = new RegExp(searchString, 'g');
  let editorState = claim.editorState;
  const selectionsToReplace = [];
  const newEntitySelections = [];
  const blockMap = editorState.getCurrentContent().getBlockMap();
  const sameString = searchString === replaceString;
  blockMap.forEach((contentBlock) => {
    let offset = 0;
    findWithRegex(regex, contentBlock, (start, end) => {
      const key = contentBlock.getEntityAt(start);
      //console.log('FOUND', start, end, key);
      // Can short circuit because we are just about to replace an
      // entity string with itself.
      if (key && key === entityKey && sameString)
        return;
      if (key === entityKey || key === null || entityKey === null) { //ek===null means delete
        const blockKey = contentBlock.getKey();
        const blockSelection = SelectionState
              .createEmpty(blockKey)
              .merge({
                anchorOffset: start+offset,
                focusOffset: end+offset,
              });
        offset += replaceString.length - searchString.length;
        if (key === entityKey) {
          selectionsToReplace.push(blockSelection);
        }
        if (everywhere && key === null) {
          newEntitySelections.push(blockSelection);
        }
        if (entityKey === null) {
          selectionsToReplace.push(blockSelection);
        }
      };
    });
  });

  //console.log('Targets', selectionsToReplace);
  //console.log('New Ents', newEntitySelections);
  
  let contentState = editorState.getCurrentContent();
  
  newEntitySelections.forEach(selectionState => {
    contentState = Modifier.applyEntity(contentState, selectionState, entityKey);
  });
  
  selectionsToReplace.forEach(selectionState => {
    //console.log('* Replacing', selectionState);
    contentState = Modifier.replaceText(
      contentState,
      selectionState,
      replaceString,
      null,
      entityKey
    );
  });

  editorState = EditorState.push(editorState, contentState);
  editorState = EditorState.moveSelectionToEnd(editorState);
  claim.updateEditorState(editorState);
  //return EditorState.push(editorState, contentState);
});


// Toggle comments to entities
export const toggleComment = action((claim, entityMap) => {
  let regex = /"[^"]+"?/gi;
  if (!claim.showWD)
    regex = /"..."/gi;
  //const regex = new RegExp(searchString, 'g');
  const editorState = claim.editorState;
  const selectionsToReplace = [];
  const blockMap = editorState.getCurrentContent().getBlockMap();
  blockMap.forEach((contentBlock) => {
    findWithRegex(regex, contentBlock, (start, end) => {
      const key = contentBlock.getEntityAt(start);
      //console.log('ENT', !key || editorState.getCurrentContent().getEntity(key));
      const text = key ? editorState.getCurrentContent().getEntity(key).data.text :
            contentBlock.getText().slice(start, end);
      //console.log('FOUND', start, end, key);
      const blockKey = contentBlock.getKey();
      const blockSelection = SelectionState
            .createEmpty(blockKey)
            .merge({
              anchorOffset: start,
              focusOffset: end,
            });
      selectionsToReplace.push({selection: blockSelection, text: text, key: key});
    });
  });

  //console.log('Targets', selectionsToReplace);
  let contentState = editorState.getCurrentContent();
  
  selectionsToReplace.forEach(item => {
    const selectionState = item.selection;
    // item.key means that we have found a comment placeholder
    const willHide = !item.key
    if (willHide) {
      contentState = contentState.createEntity('TOKEN', 'IMMUTABLE', {text: item.text});
      item.key = contentState.getLastCreatedEntityKey();
    }
    //console.log('* Replacing', item);
    contentState = Modifier.replaceText(
      contentState,
      selectionState,
      willHide ? '"..."' : item.text,
      null,
      willHide ? item.key : null, // set to null to remove entity
    );
  });

  claim.showWD = !claim.showWD;
  claim.editorState = EditorState.push(editorState, contentState);
  Object.entries(entityMap).map(([k,v], idx) =>
                                replaceString(claim, v.text, v.text, k, true));
  claim.updateTree();
  //return EditorState.push(editorState, contentState);
});
                             

// --------------------------------------------------------------------------------
const TreeMixin = {
  indexOf() { return this.parent.children.indexOf(this); },
  canMoveLeft() { return this.indexOf() !== 0; },
  canMoveRight() { return this.indexOf() < this.parent.children.length-1; },

  moveChildLeft(claim) {
    const index = claim.indexOf();
    const items = this.children;
    this.children.splice(index-1, 2, items[index], items[index-1]);
    this.claimSet.numberClaims();
  },
  
  moveChildRight(claim) {
    const index = claim.indexOf();
    const items = this.children;
    this.children.splice(index, 2, items[index+1], items[index]);
    this.claimSet.numberClaims();
  },

  addChild(editorState, claimType, claimClass, preamble, drawFig) {
    const claim = new Claim(this.claimSet, editorState, this, preamble, claimType, claimClass);
    claim.drawFig = drawFig;
    this.children.splice(this.children.length, 0, claim);
    console.log("Added", toJS(this.claimSet));
    this.claimSet.numberClaims();
    claim.updateTree();
  },
  
  deleteChild(claim) {
    const index = claim.indexOf();
    this.children.splice(index, 1);
    console.log('Deleted', toJS(this.children));
    this.claimSet.numberClaims();
  },
};

// --------------------------------------------------------------------------------

export class Claim {
  uid = String(Math.floor(Math.random() * 1000000));
  claimSet = null;
  editorState = null;
  parent = null;
  preamble = '';
  claimType = 'ind';
  claimClass = 'method';
  entityKey = null; // this is for dependent claims
  children = [];  // this is the dependent claims
  drawFig = true;  // should we draw it?
  figType = 'process' // process or apparatus
  claimNum = 1;
  figNum = 'NO_NUM';
  showWD = true;   // show the written description text in the editor?
  viewOpen = true; // is the editor open?
  makeClaim = true;  // generate a claim for this one...
  tree = null;  // this is the "tree form" of the claim used for rendering
  words = ''; // used to alice test
  oldWords = '';
  aliceScore = null;
  lastAliceTime = 0;
  hlThreshold = 1;
  
  constructor(claimSet, editorState, parent, preamble, claimType, claimClass) {
    makeAutoObservable(this);
    this.claimSet = claimSet;
    this.editorState = editorState;
    this.parent = parent;
    this.preamble = preamble;
    this.claimType = claimType;
    this.claimClass = claimClass;
  }

  static claimFromJSON(json, claimSet, parent) {
    const get = (val, defaultVal) => {
      return Object.is(val, undefined) ? defaultVal : val;
    }
    const claim =
	  new Claim(claimSet,
                    EditorState.createWithContent(convertFromRaw(json.editorState), decorator),
                    parent, json.preamble, json.claimType, json.claimClass);
    //console.log('>>>>', json);
    claim.drawFig = get(json.drawFig, true);
    claim.figNum = get(json.figNum, 3);
    claim.figType = get(json.figType, 'process');
    claim.showWD = get(json.showWD, true);
    claim.viewOpen = get(json.viewOpen, true);
    claim.makeClaim = get(json.makeClaim, true);
    if (claim.claimType !== 'ind') {
      claim.entityKey = json.entity; // no key yet
    }
    for (const child of json.children) {
      claim.children.push(Claim.claimFromJSON(child, claimSet, claim));
    }
    //console.log('++++', claim);
    return claim;
  }

  toJSON() {
    return {claimSet: null, parent: null,
            editorState: convertToRaw(this.editorState.getCurrentContent()),
            preamble: this.preamble, claimType: this.claimType,
            claimClass: this.claimClass, entity: this.entity(), // note conversion to entity text
            children: this.children.map((claim) => claim.toJSON()),
            drawFig: this.drawFig, figType: this.figType, figNum: this.figNum,
            showWD: this.showWD, viewOpen: this.viewOpen, makeClaim: this.makeClaim,
            tree: null,
           }
  }

  updateEditorState(editorState) {
    this.editorState = editorState;
    // FIXME:  this is only necessary if we need to keep a view in sync with
    // the editor state.  Could maybe make this more efficient by either
    // periodically calling it or by providing hints as to whether this should
    // be peformed (only when Figs/Text/Claims are visible).
    // Could we do this asyncronously?  Just when dirty?
    this.updateTree();
    this.getAliceScoreFromServer(false);
    this.claimSet.setDirty(true);
  }

  updateTree() {
    this.tree = this.claimTree();
    //this.getAliceScoreFromServer();
  }

  setClaimClass(val) { this.claimClass = val; }

  entity() {
    const record = this.claimSet.entityMap[this.entityKey];
    if (record)
      return record.text
    return null;
  }

  traverseUp(claim, callback) {
    console.log('TU', claim, this.claimSet);
    callback(claim);
    if (claim.parent) this.traverseUp(claim.parent, callback);
  }

  traverseDown(claim, callback) {
    callback(claim);
    claim.children.map((c) => this.traverseDown(c, callback));
  }

  toggleDrawFig() {
    if (!this.drawFig) {
      this.traverseUp(this, (c) => c.drawFig = true);
    }
    else { // need to shut down all chidren
      this.traverseDown(this, (c) => c.drawFig = false);
    }
    this.claimSet.numberClaims();
  }

  toggleViewOpen() { this.viewOpen = !this.viewOpen; }

  toggleMakeClaim() {
    this.makeClaim = !this.makeClaim;
    if (!this.makeClaim) this.traverseDown(this, (c) => c.makeClaim = false);
    if (this.makeClaim) this.traverseUp(this, (c) => c.makeClaim = true);
    this.claimSet.numberClaims();
  }

  setFigType(figType) {
    this.figType = figType;
    this.traverseDown(this, (c) => c.figType = figType);
  }

  // Sets the entity for a dependent
  setEntity(entityKey) {
    this.entityKey = entityKey;
    //this.updateTree();
  }

  setClaimNum(num) { this.claimNum = num; }
  setFigNum(num) { this.figNum = num; }

  getEntityAtSelection() {
    const selection = this.editorState.getSelection();
    const content = this.editorState.getCurrentContent();
    const block = content.getBlockForKey(selection.getAnchorKey());
    const selStart = selection.getStartOffset();
    return block.getEntityAt(selStart);
  }

  getAliceScoreFromServer(force) {
    if (this.claimType !== 'ind' || !this.claimSet.doAliceScoring) return;
    //console.log('Lengths', this.words.length, this.oldWords.length);
    if (!force && Date.now() - this.lastAliceTime < 2000) return;
    if (force || this.words.length !== this.oldWords.length ||
        this.words !== this.oldWords) {
      console.log('Getting score from server: ', this.uid, this.words.length, this.oldWords.length);
      if (socket && this.words) {
        socket.emit('alice_test', {claim: this.words, claimid: this.uid});
      }
    }
  }

  setAliceScore(result) {
    //console.log('Setting Alice result', result)
    this.aliceScore = result;
    this.lastAliceTime = Date.now();
  }

  setHlThreshold(val) {
    //console.log('Setting ', val);
    this.hlThreshold = val;
  }

  // This creates a tree representation of the from the editor state, so that
  // indented blocks appear as child elements
  // It also patches up inline comments, etc.
  // FIXME:  this function should probably not be relying on converting the content to raw
  claimTree() {
    this.oldWords = this.words;
    this.words = this.claimClass;
    const content = this.editorState.getCurrentContent();
    const rawContent = convertToRaw(content);
    const blocks = rawContent.blocks;
    //console.log('CLAIMTREE', rawContent);
    const getClaimText = (text) => {
      const txt = text.replace(/"[^"]*"/g, '');
      this.words += " " + txt;
      return txt.trim();
    };
    const getShortText = (text, entityRanges) => {
      if (entityRanges.length === 0) return getClaimText(text); // no entities, possibly open comments
      const eRange = entityRanges[0];
      var rval = text.substr(eRange.offset, eRange.length);
      if (rval.startsWith('"')) return getClaimText(text);
      return rval;
    }
    // Check to see if entity is hidden by === '"..."'
    // If so, we need to pull the entity.  Painful, but the right thing to do.
    const getWDText = (block) => {
      const match = block.text.match(/"(.*)"/);
      if (!match) return '';
      if (match[1] === '...') { // hidden
        const ek = block.entityRanges[block.entityRanges.length-1].key;
        const e = rawContent.entityMap[ek];
        return e.data.text.substring(1, e.data.text.length-1);
      }
      return match[1];
    };
    let refNum = 0;
    //console.log('CLAIMTREE', toJS(this), this.figNum);
    const root = {preamble: blocks.length ? getClaimText(blocks[0].text) : '', 
                  claimType: this.claimType, claimClass: this.claimClass,
                  entity: this.entity(), children: [],
                  shortText: getShortText(blocks[0].text, blocks[0].entityRanges),
                  wdText: getWDText(blocks[0]), indent: 0, figNum: this.figNum,
                  refNum: this.figNum + (refNum+'').padStart(2, '0')};
    if (blocks.length < 2) return root;
    let tree = root;
    let level = 0;
    let stack = [];
    // We are skipping the first element as it is always the transitional part
    for (let idx=1 ; idx<blocks.length; idx++) {
      refNum += 1;
      const block = blocks[idx];
      const node = {text: getClaimText(block.text),
                    wdText: getWDText(block), indent: 0,                   
                    shortText: getShortText(block.text, block.entityRanges),
                    children: [], refNum: this.figNum + (refNum+'').padStart(2, '0')};
      if (block.type === 'unstyled')
        node.indent = 0;
      else
        node.indent = block.depth + 1;

      // First check to see if we are only WD
      // If so we attach the text to the previous node
      if (!node.text && node.wdText) {
        //console.log('Here', idx, node.wdText);
        if (idx === 1)
          tree.wdText += ' ' + node.wdText;  // if we are first, we attach to parent
        else
          tree.children[tree.children.length-1].wdText += ' ' + node.wdText;
      }
      else if (node.indent > level) {
        //console.log('Pushing ...', node.indent, level);
        level = node.indent;
        stack.push(tree);
        if (tree.children.length)
          tree = tree.children[tree.children.length-1];
        tree.children.push(node);
      }
      else if (node.indent < level) {
        //console.log('Popping ...', node.indent, level);
        level = node.indent;
        tree = stack.pop();
        tree.children.push(node);
      }
      else if (node.indent === level) {
        //console.log('Adding ...');
        tree.children.push(node);
      }
    };
    return root;
  }

}

Object.assign(Claim.prototype, TreeMixin);


// --------------------------------------------------------------------------------


export class ClaimSet {
  uid = String(Math.floor(Math.random() * 1000000));
  entityMap = {};
  children = [];
  parent = null;
  claimSet = this;  // must have this for TreeMixin to work
  dirty = false;
  figBase = 3;
  doAliceScoring = false;
  
  constructor() {
    makeAutoObservable(this);
  }

  static claimSetFromJSON(json) {
    console.log('Loading claimset', json);
    const claimset = new ClaimSet(); 
    claimset.figBase = json.figBase;
    for (const claim of json.children) {
      claimset.children.push(Claim.claimFromJSON(claim, claimset, claimset));
    }
    //console.log('Claimset before', claimset.dumpClaims());
    claimset.updateEntityMap();
    //console.log('Claimset after', claimset.dumpClaims());

    // Now we need to walk the tree to fix up entity references
    // And once those are done, we can make a clean claim tree for each
    for (const claim of claimset.children) {
      walkTree(claim, (node) => {
        if (node.entityKey)
          node.entityKey = claimset.lookupEntity(node.entityKey);
	node.updateTree();
      });
    }
    //console.log("CLAIMS", toJS(claimset));
    //for (const claim of claimset.children)
    //  console.log('CHILD', toJS(claim));
    claimset.numberClaims();
    return claimset;
  }

  assign(claimSet) {
    this.entityMap = claimSet.entityMap;
    this.children = claimSet.children;
    this.parent = null;
    //this.claimSet = claimSet;
    this.figBase = claimSet.figBase;
    this.numberClaims();
  }

  setFigBase(figBase) { this.figBase = figBase; }

  setDirty(bool) { this.dirty = bool; }

  setDoAliceScoring(val) { this.doAliceScoring = val; }
  
  numberClaims() {
    let claimCounter = 0;
    let figCounter = 1;
    this.children.map((node, idx) => {
      return walkTree(node, (claim) => {
        //console.log('Visiting node', toJS(node));
        if (claim.makeClaim) {
          claimCounter += 1;
          claim.setClaimNum(claimCounter);
          claim.setFigNum(0);
        }
        else {
          claim.setClaimNum("[ ]")
        }
        if (claim.drawFig) {
	  claim.setFigNum(this.figBase + String.fromCharCode(64 + figCounter));
	  figCounter += 1;
        }
        claim.updateTree();
      });
    });
  }

  filterEntities(str) {
    str = str.toUpperCase();
    //console.log('FILTER', str);
    const items = [];
    for (const key of Object.keys(this.entityMap)) {
      //console.log(toJS(this.entityMap[key]));
      if (this.entityMap[key].text.toUpperCase().startsWith(str))
	items.push(this.entityMap[key].text);
    }
    return items;
  }
    
  // Traverses all editors, looking for entity tokens and
  // adding entity to the map for new ones, or updating existing ones.
  // Skips "..." entitites ...
  updateEntityMap() {
    //console.log('Updating entity map', toJS(this.entityMap));
    this.entityMap = {};
    const traverse = (claims) => {
      for (const claim of claims) {
        const editorState = claim.editorState; 
        const content = editorState.getCurrentContent();
        const blockMap = content.getBlockMap();
        blockMap.forEach((block) => (
          block.findEntityRanges(
            (c) => {
              const ek = c.getEntity();
              if (!ek) return false;
              const e = content.getEntity(ek);
              return e !== null && e.getType() === 'TOKEN' && e.getMutability() === 'MUTABLE';
            },
            (start, end) => {
              // We need to first look for entity by string because when we load a
              // file, same entities in different editors will get different keys
              let key = this.lookupEntity(block.getText().slice(start, end))
              // If we don't match one by text, we'll just use its key
              if (!key) {
                key = block.getEntityAt(start);
              }
	      //console.log('Entity at', start, end, key);
              if (key in this.entityMap) {
                this.entityMap[key].blocks.push({start, end, block, claim});
              }
              else {
                this.entityMap[key] = {text: block.getText().slice(start, end), blocks: []};
              }
            }
          )));
        traverse(claim.children);
      }
    };
    traverse(this.children);
    //console.log('EMAP', toJS(cseModel.entityMap));
  }

  deleteEntity(eString, key) {
    console.log('Deleting', eString, key);
    this.globalReplace(eString, eString, null, false);
    delete this.entityMap[key];
    this.updateEntityMap();
  }

  addEntityAtSelection() {
    const model = this;
    const claim = this.findSelectedClaim(false);
    if (!claim) {
      console.log('No ed selected');
      return;
    }
    const selection = claim.editorState.getSelection();
    let content = claim.editorState.getCurrentContent();
    const block = content.getBlockForKey(selection.getAnchorKey());
    const text = block.getText().slice(selection.getStartOffset(), selection.getEndOffset()).trim();
    console.log('TEXT', text);
    if (text.length === 0) {
      console.log("Empty entity? returning...");
      return;
    }
    let ek = this.lookupEntity(text);
    console.log('ADDING entity', toJS(claim), selection, content, block, text);
    if (!ek) {
      console.log('Making new entity');
      content = content.createEntity('TOKEN', 'MUTABLE', null);
      ek = content.getLastCreatedEntityKey();
    }
    content = Modifier.applyEntity(content, selection, ek);
    let newState = EditorState.set(claim.editorState, {currentContent: content});
    // now search for other occurrences of the string...
    //newState = createEntities(block.getText().slice(selection.getStartOffset(), selection.getEndOffset()),
    //                          ek, newState);
    claim.updateEditorState(newState);
    this.globalReplace(text, text, ek, true);
    model.updateEntityMap();
  }

  addInlineEntity(claim, editorState, chars) {
    //console.log(claim, editorState, chars);
    let selection = editorState.getSelection();
    let content = editorState.getCurrentContent();
    const block = content.getBlockForKey(selection.getAnchorKey());
    const text = block.getText();
    let start = text.indexOf('@');

    if (chars === '@') { // FIXME:  for some reason '$' does not work...
      // first look to see if we've already typed a @ on this line
      start = text.indexOf('@');
      const end = selection.getEndOffset();
      if (start === -1) {
        console.log('First @');
        return null;
      }
      const eString = text.slice(start+1, end);
      let ek = this.lookupEntity(eString);
      console.log('ADDING ENT', start, end, eString, ek);
      //console.log(toJS(this.entityMap));
      if (!ek) {
        content = content.createEntity('TOKEN', 'MUTABLE', null);
        ek = content.getLastCreatedEntityKey();
      }
      content = Modifier.applyEntity(content, selection, ek);
      content = Modifier.replaceText(
        content, 
        selection.merge({anchorOffset: start, focusOffset: end}),
        eString,
        null,
        ek);
      editorState = EditorState.set(editorState, {currentContent: content});
      editorState = EditorState.forceSelection(
        editorState,
        selection.merge({anchorOffset: end-1, focusOffset: end-1})
      ); 
      claim.updateEditorState(editorState);
      this.updateEntityMap();
      return 'handled';
    }
  }



  lookupEntity(eString) {
    for (const [k,v] of Object.entries(this.entityMap)) {
      //console.log('LOOOKUP', k, toJS(v));
      if (v.text === eString)
        return k;
    }
    return null;
  }
  
  findSelectedClaim(collapsedOK) {
    //console.log('FIND Selected Editor', toJS(this.children));
    let traverse = (claims) => {
      for (const claim of claims) {
        const editorState = claim.editorState;
        //const content = editorState.getCurrentContent();
        const selection = editorState.getSelection();
        console.log(selection, selection.getStartOffset(), selection.getEndOffset());
        if (selection && (collapsedOK || selection.getStartOffset() !== selection.getEndOffset()))
          return claim;
        const theEd = traverse(claim.children);
        if (theEd) return theEd;
      }
      return null;
    };
    return traverse(this.children);
  }

  globalReplace(search, replace, entityKey, everywhere) {
    const _globalReplace = (claims, search, replace, entityKey, everywhere) => {
      for (const claim of claims) {
        replaceString(claim, search, replace, entityKey, everywhere);
        claim.updateTree();
        _globalReplace(claim.children, search, replace, entityKey, everywhere);
      }
    }
    return _globalReplace(this.children, search, replace, entityKey, everywhere);
  }

  dumpClaims() {
    const newMap = {};
    Object.entries(this.entityMap).map(([k,v]) => newMap[k] = v.text);
    return {
      entityMap: newMap,
      children: this.children.map((claim) => claim.toJSON()),
      parent: null,
      claimSet: null,
      figBase: this.figBase,
    }
  }

}

Object.assign(ClaimSet.prototype, TreeMixin);

// ----------------------------------------------------------------------------

// This is the global model
export class ClaimWorkShop {
  claimSet = null;
  selectedEntityKey = null;
  doc = {name: '', id: null}; //null;  // this is the google doc
  googleUser = null;
  user = null;
  connected = false;
  loginStatus = null; // EMAIL_SENT, KEY_SENT, LOGIN_OK, NO_USER
  version = window.VERSION;
  updateAvailable = false;

  constructor() {
    makeAutoObservable(this);
    setConnectCallBack((msg)=>{this.connectCallBack(msg)});
    setConnectErrCallBack((err)=>{this.connectErrCallBack(err)});
  }

  connectCallBack(msg) {
    console.log('Connect callback', msg);
    this.initHandlers();
    this.connected = true;
    if (this.version !== msg.version) {
      this.updateAvailable = true;
    }
  }
  
  connectErrCallBack(err) {
    console.log('Error callback', JSON.stringify(err));
    this.connected = false;
  }

  initHandlers() {
    socket.on('save_response', (msg) => {
      console.log(msg);
    });

    socket.on('load_response', (msg) => {
      console.log(msg);
      this.claimSet = ClaimSet.claimSetFromJSON(msg.claimSet);
    });

    socket.on('login_response', (msg) => {
      console.log('Login response', msg);
      this.setLoginStatus(msg.status);
      if (this.loginStatus === 'LOGIN_OK')
        this.setUser(msg.user);
    });

    socket.on('alice_test_response', (msg) => {
      console.log('ALICE', msg);
      for (const claim of this.claimSet.children) {
        console.log(msg.claimid, claim.uid);
        if (claim.uid === msg.claimid) {
          claim.setAliceScore(msg.result);
        }
      }
    });
  }

  loadExample(num) {
    this.claimSet = ClaimSet.claimSetFromJSON(examples[num]);
  }

  setClaimSet(claimSet) { this.claimSet = claimSet; }

  newClaimSet() {
    this.claimSet = new ClaimSet();
    this.doc = {name : "", id: null }; //null;
    this.addNewClaim('ind', 'method');
  }

  addNewClaim(claimType, claimClass) {
    this.claimSet.addChild(EditorState.createEmpty(decorator), claimType, claimClass, '[preamble]', true);
  }

  save() {
    socket.emit('save', {claimSet: this.claimSet.dumpClaims()});
  }
  
  load() {
    socket.emit('load', {filename: 'foo'});
  }

  setDoc(doc) { this.doc = doc; }
  setDocName(name) { this.doc.name = name; }
  setUpdateAvailable(bool) { this.updateAvailable = bool; }

  toJSON() {
    return this.claimSet.dumpClaims();
  }

  toHTML() {
    let result = '<html><head><meta charset="utf-8"></head><body>';
    result += wdTemplate({claimSet: this.claimSet, heading: 'Written Description'});
    result += claimTemplate({claimSet: this.claimSet, heading: 'Claims'});
    result += drawingTemplate({claimSet: this.claimSet, heading: 'Drawings'});
    result += '</body></html>';
    return result;
  }

  figsToHTML() {
    let result = `
<html>
<head>
<meta charset="utf-8">
<style>
* { font-family: sans-serif; }
div {
  page-break-inside: avoid;
  font-size: 14pt;
}
h2 {
  page-break-inside: avoid;
  font-size: 24pt;
}
center { page-break-indide: avoid;
}
@page {
  margin-left: 1.5in;
  margin-right: 1.5in;
  margin-top: 1in;
  margin-bottom: 1in;
}
</style>
</head>

<body>`;
    result += drawingTemplate({claimSet: this.claimSet});
    result += '</body></html>';
    return result;
  }

  shareWithWord() {
    let wd = wd2JSON(this.claimSet);
    console.log('WDJSON', wd);
    let claims = claims2JSON(this.claimSet);
    console.log('ClaimsJSON', claims);
    socket.emit('share', {key: this.user.key, drawing_text: wd, claims: claims});
  }

  
  setGoogleUser(userProfile) { this.googleUser = userProfile; }
  getGoogleUser() {
    if (this.googleUser)
      return {name: this.googleUser['name'], email: this.googleUser['email']};
    else
      return '[Not connected]'
  }
  setUser(userProfile) { this.user = userProfile; }
  setLoginStatus(val) { this.loginStatus = val; }
  
  login(key) {
    console.log('Sending key', key);
    this.setLoginStatus('KEY_SENT');
    if (this.user)
      this.user.key = key;
    else
      this.user = {key: key}
    socket.emit('login', {user: this.user, sendmail: true, url: window.location.href});
  }

  getAliceScores(force) {
    for (const claim of this.claimSet.children) {
      claim.getAliceScoreFromServer(force);
    }
  }
}

